import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/blog/shopify-seo-optimierung-guide";
const TITLE = "Shopify SEO Optimierung: Leitfaden für mehr Sichtbarkeit und Umsatz";
const DESCRIPTION =
  "Shopify SEO Optimierung Schritt für Schritt: Kategoriestruktur, Varianten und Duplicate Content, Ladezeit, strukturierte Daten, Inhalte und Sichtbarkeit in KI-Antworten. Mit Prüfliste.";
const UPDATED = "2026-09-10";

const ANSWER =
  "Shopify SEO wirkt in dieser Reihenfolge: technische Fehler beheben, Ladezeit verbessern, Kategorieseiten inhaltlich ausbauen, Produktseiten mit strukturierten Daten versehen und erst danach Ratgeberinhalte aufbauen. Die größten ungenutzten Rankings liegen in fast jedem Shop auf den Kategorieseiten, weil dort allgemeine Suchbegriffe entschieden werden. Erste Bewegungen sind meist nach vier bis zwölf Wochen sichtbar.";

const faqs = [
  {
    q: "Ist Shopify gut für SEO?",
    a: "Ja. Shopify liefert saubere technische Grundlagen, erzwingt aber eine feste URL-Struktur mit /products/ und /collections/. Die typischen Probleme sind selbstgemacht: dünne Kategorietexte, doppelte Varianten-URLs und zu viele Apps, die die Ladezeit belasten.",
  },
  {
    q: "Wie optimiere ich meinen Shopify Shop für Suchmaschinen?",
    a: "Zuerst technisch aufräumen: Canonicals für Varianten- und Filter-URLs, Sitemap ohne Weiterleitungen, keine indexierten Suchergebnisseiten. Danach Kategorieseiten mit Einordnungstext, Filtern und internen Links ausbauen, Produktseiten mit strukturierten Daten versehen und zuletzt Ratgeberinhalte ergänzen.",
  },
  {
    q: "Welche Seiten bringen bei Shopify die meisten Rankings?",
    a: "Kategorieseiten für allgemeine Suchbegriffe, Produktseiten für konkrete Produkt- und Markennamen, Ratgeberinhalte für Fragen. Die größte ungenutzte Chance liegt fast immer bei den Kategorieseiten.",
  },
  {
    q: "Wie vermeide ich Duplicate Content bei Varianten?",
    a: "Varianten-URLs mit ?variant= per Canonical auf die Hauptproduktseite verweisen lassen und Produkte, die über mehrere Kategorien erreichbar sind, konsequent auf eine kanonische URL zeigen.",
  },
  {
    q: "Wie lange dauert es, bis Shopify SEO wirkt?",
    a: "Technische Korrekturen und Ladezeitverbesserungen wirken oft innerhalb weniger Wochen, inhaltliche Arbeit an Kategorie- und Ratgeberseiten meist nach zwei bis sechs Monaten. Garantien für Platzierungen gibt es nicht, planbar sind nur Umsetzung und Messung.",
  },
  {
    q: "Wie hängen SEO und Conversion zusammen?",
    a: "Rankings bringen Besucher, die Kaufabschlussrate bringt Umsatz. Beides greift ineinander: Schnellere Seiten und klare Struktur verbessern Nutzersignale, und bessere Nutzersignale stützen Rankings.",
  },
  {
    q: "Wie werde ich in KI-Antworten wie ChatGPT oder Perplexity zitiert?",
    a: "Mit eindeutigen Fakten auf der Seite: konkrete Zahlen, klare Definitionen, Fragen mit kurzen Antworten, strukturierte Daten und eine maschinenlesbare Kurzfassung. KI-Systeme zitieren gern das, was sich ohne Interpretation übernehmen lässt.",
  },
];

export const Route = createFileRoute("/blog/shopify-seo-optimierung-guide")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify SEO",
      updated: UPDATED,
      breadcrumb: { name: "Blog", path: PATH },
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <ArticlePage
      kicker="Blog · SEO"
      title="Shopify SEO Optimierung: die Reihenfolge, die Umsatz bringt"
      intro="SEO für Shopify scheitert selten an der Plattform, sondern an dünnen Kategorieseiten, doppelten URLs und langsamen Themes. Dieser Leitfaden zeigt, was in welcher Reihenfolge zu tun ist, wie Sie den Fortschritt messen und wie Ihr Shop zusätzlich in KI-Antworten auftaucht."
      source="blog-shopify-seo-optimierung-guide"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/blog", label: "Blog" }}
      sections={[
        {
          h: "Struktur zuerst: Kategorien schlagen Produkte",
          p: [
            "Allgemeine Suchbegriffe wie „Badematten waschbar“ ranken über Kategorieseiten, nicht über einzelne Produkte. Genau diese Seiten bestehen in den meisten Shops aus einer Überschrift und einem Produktraster.",
            "Eine Kategorieseite braucht einen kurzen einordnenden Text oben, sinnvolle Filter, interne Links auf verwandte Kategorien und einen ausführlicheren Abschnitt unter den Produkten. Erst dann hat Google überhaupt etwas zu bewerten.",
            "Praktisch bewährt sich eine feste Gliederung pro Kategorie: Wofür ist die Kategorie, welche Auswahlkriterien gibt es, welche Unterkategorien sind verwandt, welche Fragen stellen Käufer.",
          ],
        },
        {
          h: "Technische Basis in Shopify",
          p: [
            "Die URL-Struktur ist festgelegt und das ist in Ordnung. Wichtig sind saubere Canonicals, keine indexierten Filter-URLs und eine Sitemap ohne Weiterleitungen.",
          ],
          list: [
            "Varianten-URLs (?variant=) per Canonical auf das Hauptprodukt",
            "Produkte kanonisch auf /products/ statt /collections/…/products/",
            "Suchergebnis- und Tag-Seiten von der Indexierung ausschließen",
            "Ladezeit: Apps reduzieren, Bilder in modernen Formaten, Fonts lokal einbinden",
            "Strukturierte Daten für Produkt, Bewertung, Breadcrumb und Breadcrumb-Pfad",
            "Search Console prüfen: ausgeschlossene Seiten, Weiterleitungsketten, Soft-404",
          ],
        },
        {
          h: "Ladezeit als Rankingfaktor und Umsatzfaktor",
          p: [
            "Jede zusätzliche Sekunde Ladezeit kostet auf Mobilgeräten Sichtbarkeit und Käufe zugleich. In Shopify entstehen die meisten Verzögerungen durch Apps, die auf jeder Seite Skripte laden, obwohl sie nur an einer Stelle gebraucht werden.",
            "Der pragmatische Weg: Apps inventarisieren, ungenutzte entfernen, verbliebene Skripte auf die Seiten begrenzen, auf denen sie wirken, Bilder in WebP ausliefern und das erste sichtbare Bild bevorzugt laden.",
          ],
        },
        {
          h: "Inhalte, die auch verkaufen",
          p: [
            "Ratgeberinhalte funktionieren, wenn sie eine echte Kaufentscheidung stützen: Materialvergleich, Größenwahl, Pflege, Anwendungsfälle. Reine Keyword-Texte ohne Substanz verlieren seit den letzten Google-Updates schnell an Sichtbarkeit.",
            "Jeder Artikel braucht einen klaren nächsten Schritt in den Shop, sonst erzeugt er Traffic ohne Umsatz.",
          ],
        },
        {
          h: "Sichtbarkeit in KI-Antworten",
          p: [
            "ChatGPT, Perplexity, Gemini und Google AI Overviews zitieren Seiten, aus denen sich eine Aussage ohne Interpretation übernehmen lässt. Das bevorzugt Formate mit klaren Fakten.",
          ],
          list: [
            "Kurze, direkt beantwortete Fragen mit konkreten Zahlen",
            "Definitionen und Preisangaben im Klartext statt in Bildern",
            "FAQ- und Produkt-Schema als strukturierte Daten",
            "Eine llms.txt mit Kurzprofil und Seitenverzeichnis",
            "Konsistente Angaben über alle Seiten hinweg, Widersprüche kosten Zitierbarkeit",
          ],
        },
        {
          h: "Prüfliste für die ersten 30 Tage",
          p: [
            "Diese Reihenfolge hat sich in Kundenprojekten bewährt, weil sie mit den Punkten beginnt, die ohne neue Inhalte wirken.",
          ],
          list: [
            "Woche 1: Indexierungsfehler, Canonicals, Weiterleitungsketten und Sitemap bereinigen",
            "Woche 1: Apps und Skripte aufräumen, Bilder komprimieren, mobile Ladezeit messen",
            "Woche 2: Die fünf umsatzstärksten Kategorieseiten inhaltlich ausbauen",
            "Woche 3: Produktseiten mit strukturierten Daten, klaren Titeln und Beschreibungen versehen",
            "Woche 4: Zwei Ratgeberinhalte zu echten Kundenfragen veröffentlichen und intern verlinken",
          ],
        },
        {
          h: "Fortschritt messen",
          p: [
            "Aussagekräftig sind Impressionen und Klicks je Seitentyp in der Search Console, die durchschnittliche Position der wichtigsten Kategorieseiten sowie Umsatz pro Session aus organischer Suche. Rankings allein sagen wenig, wenn die Seiten die Besucher nicht in Käufe überführen.",
            "Sinnvoll ist ein monatlicher Blick statt täglicher Kontrolle, weil Schwankungen im Wochenverlauf normal sind.",
          ],
        },
        {
          h: "Was zuerst wirkt",
          p: [
            "Reihenfolge in der Praxis: technische Fehler beheben, Ladezeit verbessern, Kategorieseiten ausbauen, dann Ratgeberinhalte. Wer mit Content beginnt, während der Shop zwei Sekunden zu langsam lädt, arbeitet gegen die eigene Sichtbarkeit.",
            "Wenn Sie das nicht selbst umsetzen möchten: Ich übernehme Shopify SEO als Projekt oder begleitend, Entwicklung und Theme-Arbeit mit 110 € netto pro Stunde.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-seo", label: "Shopify SEO umsetzen lassen" },
        { to: "/ratgeber/shopify-pagespeed-optimieren", label: "Pagespeed verbessern" },
        { to: "/ratgeber/shopify-produktseite-optimieren", label: "Produktseite optimieren" },
        { to: "/blog/shopify-checkout-optimieren", label: "Checkout optimieren" },
        { to: "/kontakt", label: "SEO-Projekt anfragen" },
      ]}
    />
  );
}
